// packages/web/src/components/marketplace/CatalogSelector.tsx
// Catalog switching UI component

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box, Typography, Button, Menu, MenuItem, Chip
} from '@mui/material';
import React from 'react';

interface CatalogOption {
  id: string;
  name: string;
  isMainCatalog: boolean;
}

interface CatalogSelectorProps {
  currentCatalog: CatalogOption | null;
  availableCatalogs: CatalogOption[];
  onCatalogChange: (catalogId: string) => void;
  loading?: boolean;
}

/**
 * Component for selecting and switching between catalogs
 */
export function CatalogSelector({
  currentCatalog,
  availableCatalogs,
  onCatalogChange,
  loading = false
}: CatalogSelectorProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCatalogSelect = (catalogId: string) => {
    onCatalogChange(catalogId);
    handleClose();
  };

  if (!currentCatalog && availableCatalogs.length <= 1) {
    return null; // Don't show selector if only one catalog
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Button
        onClick={handleClick}
        disabled={loading}
        endIcon={<KeyboardArrowDownIcon />}
        variant="outlined"
        size="small"
        sx={{
          minWidth: 200,
          justifyContent: 'space-between',
          textTransform: 'none'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            Select Catalog
          </Typography>
          {currentCatalog?.isMainCatalog && (
            <Chip
              label="Main"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 18 }}
            />
          )}
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: { minWidth: 200 }
        }}
      >
        {availableCatalogs.map((catalog) => (
          <MenuItem
            key={catalog.id}
            onClick={() => handleCatalogSelect(catalog.id)}
            selected={catalog.id === currentCatalog?.id}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Typography variant="body2">
                {catalog.name}
              </Typography>
              {catalog.isMainCatalog && (
                <Chip
                  label="Main"
                  size="small"
                  color="primary"
                  variant="filled"
                  sx={{ fontSize: '0.6rem', height: 16 }}
                />
              )}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}